import React, { useState } from 'react';
import { BarChart3, FileText, Thermometer, Shield, Download, ChevronRight, PenLine, CheckCircle2, Search, AlertCircle } from 'lucide-react';
import api from '../utils/api';

// ── Verificador de documentos ─────────────────────────────────────────────
function VerificadorDocumento() {
  const [codigo, setCodigo] = useState('');
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const verificar = async () => {
    if (!codigo.trim()) return;
    setLoading(true); setError(''); setResultado(null);
    try {
      const r = await api.get(`/informes/verificar/${codigo.trim().toUpperCase()}`);
      setResultado(r.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Documento no encontrado');
    } finally { setLoading(false); }
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header" style={{ background: 'linear-gradient(135deg, #1a1a1a, #2d2d2d)' }}>
        <div>
          <h3 style={{ color: 'white', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Search size={16} color="var(--color-primary)" /> Verificador de Documentos Firmados
          </h3>
          <p style={{ color: 'rgba(255,255,255,.5)', margin: '4px 0 0', fontSize: '0.82rem' }}>
            Introduce el código de un PNT para comprobar si ha sido firmado electrónicamente
          </p>
        </div>
      </div>
      <div className="card-body">
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <input
            className="form-control"
            style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: '1rem', flex: 1 }}
            placeholder="Ej: G004"
            value={codigo}
            onChange={e => setCodigo(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && verificar()}
            maxLength={10}
          />
          <button className="btn btn-dark" onClick={verificar} disabled={loading || !codigo.trim()}>
            {loading ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <Search size={16} />}
            Verificar
          </button>
        </div>

        {error && (
          <div className="alert alert-danger">
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {resultado && (
          <div style={{ border: `2px solid ${resultado.firma?.firmado ? '#16a34a' : '#f59e0b'}`, borderRadius: 10, overflow: 'hidden' }}>
            {/* Estado general */}
            <div style={{ background: resultado.firma?.firmado ? '#16a34a' : '#f59e0b', padding: '12px 20px', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: '1.4rem' }}>{resultado.firma?.firmado ? '✅' : '⚠️'}</span>
              <div>
                <div style={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
                  {resultado.firma?.firmado ? 'DOCUMENTO FIRMADO ELECTRÓNICAMENTE' : 'DOCUMENTO SIN FIRMA'}
                </div>
                <div style={{ color: 'rgba(255,255,255,.8)', fontSize: '0.8rem' }}>
                  Verificado el {resultado.verificado_en}
                </div>
              </div>
            </div>

            {/* Datos del documento */}
            <div style={{ padding: 20, background: 'white' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                {[
                  ['Código', resultado.documento.codigo],
                  ['Versión', `v${resultado.documento.version}`],
                  ['Título', resultado.documento.titulo],
                  ['Categoría', resultado.documento.categoria],
                  ['Estado', resultado.documento.estado],
                  ['Fecha creación', resultado.documento.fecha_creacion ? new Date(resultado.documento.fecha_creacion).toLocaleDateString('es-ES') : '—'],
                ].map(([label, valor]) => (
                  <div key={label}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--color-text-3)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
                    <div style={{ fontSize: '0.9rem', fontWeight: 500, marginTop: 2 }}>{valor}</div>
                  </div>
                ))}
              </div>

              {/* Bloque de firma */}
              {resultado.firma?.firmado && (
                <div style={{ border: '2px dashed #16a34a', borderRadius: 8, padding: 16, background: '#f0fdf4', marginTop: 8 }}>
                  <div style={{ fontSize: '0.72rem', color: '#16a34a', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 }}>
                    Registro de Firma Electrónica
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem', lineHeight: 2, color: '#333' }}>
                    {[
                      ['Firmado electrónicamente por', resultado.firma.firmado_por],
                      ['Cargo', resultado.firma.cargo],
                      ['Empresa', resultado.firma.empresa],
                      ['Fecha y hora de firma', resultado.firma.fecha_firma],
                      ['Sistema de gestión', resultado.firma.sistema],
                    ].map(([l, v]) => (
                      <div key={l}>
                        <span style={{ color: '#888', minWidth: 220, display: 'inline-block' }}>{l}:</span>
                        <strong>{v}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Botón descargar PDF */}
              <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => window.open(`/api/informes/pnt-codigo/${resultado.documento.codigo}`, '_blank')}
                >
                  <Download size={14} /> Descargar PDF con bloque de firma
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tarjeta de informe ────────────────────────────────────────────────────
const INFORMES = [
  {
    id: 'firmas',
    titulo: 'Informe de Firmas Electrónicas',
    descripcion: 'Documento oficial con todas las firmas electrónicas realizadas. Incluye nombre, cargo, fecha, hora e IP del firmante. Válido para inspección sanitaria.',
    icon: PenLine,
    color: '#e91e8c',
    bg: '#fce4f3',
    destacado: true,
    filtros: [
      { key: 'desde', label: 'Desde', type: 'date' },
      { key: 'hasta', label: 'Hasta', type: 'date' },
      { key: 'estado', label: 'Estado', type: 'select', options: [{ value: '', label: 'Todos' }, { value: 'firmado', label: 'Solo firmados' }, { value: 'rechazado', label: 'Solo rechazados' }] },
    ]
  },
  {
    id: 'pnts',
    titulo: 'Informe de PNTs y Procedimientos',
    descripcion: 'Listado completo de todos los PNTs con su estado, versión y categoría. Ideal para inspecciones.',
    icon: FileText,
    color: '#2196f3',
    bg: '#dbeafe',
    filtros: [
      { key: 'estado', label: 'Estado', type: 'select', options: [{ value: '', label: 'Todos' }, { value: 'firmado', label: 'Firmados' }, { value: 'pendiente_firma', label: 'Pendientes firma' }, { value: 'borrador', label: 'Borradores' }] },
      { key: 'desde', label: 'Desde', type: 'date' },
      { key: 'hasta', label: 'Hasta', type: 'date' },
    ]
  },
  {
    id: 'temperatura',
    titulo: 'Control de Temperatura',
    descripcion: 'Registro detallado de temperaturas y humedades del almacén con incidencias.',
    icon: Thermometer,
    color: '#ef4444',
    bg: '#fee2e2',
    filtros: [
      { key: 'desde', label: 'Desde', type: 'date' },
      { key: 'hasta', label: 'Hasta', type: 'date' },
    ]
  },
  {
    id: 'auditoria',
    titulo: 'Registro de Auditoría del Sistema',
    descripcion: 'Historial completo de todas las acciones: accesos, firmas, modificaciones y exportaciones.',
    icon: Shield,
    color: '#6366f1',
    bg: '#ede9fe',
    filtros: []
  },
];

function InformeCard({ informe }) {
  const [expanded, setExpanded] = useState(false);
  const [filtros, setFiltros] = useState({});
  const [generating, setGenerating] = useState(false);
  const Icon = informe.icon;

  const handleGenerarPDF = () => {
    setGenerating(true);
    const params = new URLSearchParams(Object.entries(filtros).filter(([, v]) => v));
    const url = `/api/informes/${informe.id}${params.toString() ? '?' + params.toString() : ''}`;
    window.open(url, '_blank');
    setTimeout(() => setGenerating(false), 1500);
  };

  return (
    <div className="card" style={{ border: informe.destacado ? `2px solid ${informe.color}` : '1px solid var(--color-gray-3)' }}>
      <div
        style={{ padding: '18px 20px', display: 'flex', gap: 16, alignItems: 'flex-start', cursor: 'pointer' }}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={{ width: 46, height: 46, borderRadius: 12, background: informe.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={22} color={informe.color} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <div style={{ fontWeight: 700, fontSize: '1rem' }}>{informe.titulo}</div>
            {informe.destacado && (
              <span style={{ background: informe.color, color: 'white', fontSize: '0.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Inspección sanitaria
              </span>
            )}
          </div>
          <p style={{ fontSize: '0.85rem', lineHeight: 1.5, margin: 0 }}>{informe.descripcion}</p>
        </div>
        <ChevronRight size={18} style={{ color: 'var(--color-text-3)', transform: expanded ? 'rotate(90deg)' : 'none', transition: 'var(--transition)', flexShrink: 0, marginTop: 4 }} />
      </div>

      {expanded && (
        <div style={{ borderTop: '1px solid var(--color-gray-3)', padding: 20 }}>
          {informe.filtros.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--color-text-3)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Filtros
              </div>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                {informe.filtros.map(f => (
                  <div key={f.key} className="form-group" style={{ margin: 0 }}>
                    <label className="form-label">{f.label}</label>
                    {f.type === 'select' ? (
                      <select className="form-control" style={{ width: 180 }} value={filtros[f.key] || ''} onChange={e => setFiltros(prev => ({ ...prev, [f.key]: e.target.value }))}>
                        {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : (
                      <input type={f.type} className="form-control" style={{ width: 160 }} value={filtros[f.key] || ''} onChange={e => setFiltros(prev => ({ ...prev, [f.key]: e.target.value }))} />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <button className="btn btn-primary" onClick={handleGenerarPDF} disabled={generating}>
            {generating ? <div className="spinner" style={{ width: 16, height: 16 }} /> : <Download size={16} />}
            {generating ? 'Generando PDF...' : 'Descargar PDF'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────
export default function InformesPage() {
  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <BarChart3 size={24} color="var(--color-primary)" /> Informes y Verificación
        </h1>
        <p>Genera informes PDF para inspección sanitaria y verifica la autenticidad de las firmas</p>
      </div>

      {/* Verificador */}
      <VerificadorDocumento />

      {/* Informes */}
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: '1rem', color: 'var(--color-text-2)', marginBottom: 14 }}>Informes disponibles</h2>
      </div>
      <div style={{ display: 'grid', gap: 14 }}>
        {INFORMES.map(informe => <InformeCard key={informe.id} informe={informe} />)}
      </div>

      {/* Guía inspección */}
      <div className="card" style={{ marginTop: 24, background: 'var(--color-gray-1)', border: 'none' }}>
        <div className="card-body">
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <div style={{ fontSize: '2rem' }}>🏥</div>
            <div>
              <div style={{ fontWeight: 700, marginBottom: 8 }}>Guía para demostrar las firmas a un inspector sanitario</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[
                  ['1. Verificar un PNT', 'Introduce el código (ej: G004) en el verificador de arriba. Muestra en pantalla el estado y la firma.'],
                  ['2. PDF con firma visible', 'Descarga el PDF de cualquier PNT firmado — incluye el bloque de firma electrónica al final.'],
                  ['3. Informe de firmas', 'Descarga el "Informe de Firmas Electrónicas" — lista oficial de todos los documentos firmados.'],
                  ['4. Historial en pantalla', 'En "Firmas Pendientes → Historial" se ve quién firmó cada documento y cuándo.'],
                ].map(([titulo, desc]) => (
                  <div key={titulo} style={{ background: 'white', borderRadius: 8, padding: 12, border: '1px solid var(--color-gray-3)' }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 4, color: 'var(--color-primary)' }}>{titulo}</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--color-text-2)', lineHeight: 1.5 }}>{desc}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
